import { Link } from "react-router-dom";
import { ApiUnavailableState, LoadingState, NotConfiguredState } from "../components/states";
import { AdminCard, EmptyInline, MetricCard } from "../components/dashboard";
import { Icon } from "../components/icons";
import { usePlatformStatus } from "../hooks/usePlatformStatus";
import { useAppContext } from "../hooks/useAppContext";

/**
 * Content & Feeds — scheduled posts and syndicated feeds ODIN publishes to your
 * server. Layout uses the ODIN design system; the stat row and cards are driven
 * by real data with honest empty states until a server is connected. No
 * fabricated posts, feeds or subscriber counts.
 */

export function Content(): JSX.Element {
  const status = usePlatformStatus();
  const { guildName } = useAppContext();
  const ready = status.kind === "ready" ? status.data : null;
  const connected = Boolean(guildName);

  return (
    <div className="ax-content" style={{ gridTemplateColumns: "1fr" }}>
      <div className="ax-workspace">
        <div className="ax-head">
          <div>
            <h1 className="ax-head__title">Content &amp; Feeds</h1>
            <p className="ax-head__sub">Scheduled posts and syndicated feeds ODIN publishes to your server.</p>
          </div>
          <Link to="/app/servers" className="ax-btn ax-btn--secondary">
            {connected ? "Schedule a post" : "Connect a server"}
          </Link>
        </div>

        {status.kind === "loading" ? <LoadingState label="Contacting the control API…" /> : null}
        {status.kind === "not-configured" ? (
          <NotConfiguredState title="Control API URL not configured">
            Set <code>PUBLIC_API_URL</code> when building the dashboard to see live status here.
          </NotConfiguredState>
        ) : null}
        {status.kind === "unavailable" ? <ApiUnavailableState detail={status.detail} /> : null}

        {ready ? (
          <>
            <div className="ax-stats4">
              <MetricCard label="Scheduled posts" icon={<Icon.calendar />} value={null} foot={connected ? "Awaiting sync" : "No server"} />
              <MetricCard label="Published (7d)" icon={<Icon.megaphone />} iconTone="success" value={null} foot={connected ? "Awaiting sync" : "No server"} />
              <MetricCard label="Active feeds" icon={<Icon.content />} value={null} foot={connected ? "Awaiting sync" : "No server"} />
              <MetricCard label="Subscribers" icon={<Icon.members />} value={null} foot={connected ? "Awaiting sync" : "No server"} />
            </div>

            <div className="ax-sections">
              <AdminCard title="Scheduled posts">
                <EmptyInline title="No scheduled posts">
                  {connected
                    ? "Schedule a post to publish it to your server automatically."
                    : "Connect a Discord server to schedule posts."}
                </EmptyInline>
              </AdminCard>

              <AdminCard title="Feeds (RSS / streams)" link={{ label: "Add a feed", to: "/app/integrations" }}>
                <EmptyInline title="No feeds connected">
                  {connected
                    ? "Connect an RSS feed or stream to syndicate updates to your server."
                    : "Connect a Discord server to add feeds."}
                </EmptyInline>
              </AdminCard>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
